import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { LogOut } from 'lucide-react'
import { LanguageSwitcher } from './LanguageSwitcher'
import { Button } from './ui/button'
import { useAuthStore } from '@/stores/auth'

interface AuthLayoutProps {
  children: React.ReactNode
  showLogout?: boolean
  maxWidth?: string
}

export function AuthLayout({ children, showLogout, maxWidth = 'max-w-md' }: AuthLayoutProps) {
  const navigate = useNavigate()
  const { t } = useTranslation('common')
  const { logout } = useAuthStore()

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/login')
    } catch (error) {
      navigate('/login')
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 py-12 px-4 sm:px-6 lg:px-8 relative">
      {/* Fixed Language Switcher & Logout */}
      <div className="fixed top-4 right-4 z-50 flex items-center gap-2">
        {showLogout && (
          <Button variant="ghost" size="icon" onClick={handleLogout} title={t('navigation.logout')}>
            <LogOut className="h-4 w-4" />
          </Button>
        )}
        <LanguageSwitcher />
      </div>

      {/* Subtle nature decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full bg-secondary/5 blur-3xl" />
      </div>

      {/* Main Content */}
      <div className={`${maxWidth} w-full relative z-10`}>
        {children}
      </div>
    </div>
  )
}
